import isNil from 'lodash/isNil';
import { formatPercentTiny, formatValueTiny } from '../formatText';
import {
  IMeasureDisplayFormatFieldsFragment,
  Maybe,
  MeasureDisplayFormatKind,
  MeasureDisplayFormatOperator,
} from '../apollo/graphqlSchema';
import { measureDisplayFormatCalculationUnitLookup } from '../builder/measures/measureDisplayFormat/utils';
import globalFormat from './GlobalFormat';
import { defaultNumberFormatOptions } from './utils';
import { ICentralYAxisSettings } from '../../components/Chart/interfaces';

export const computeMeasureValue = (
  value: number,
  calculationUnit?: Maybe<MeasureDisplayFormatOperator>,
  calculationValue?: Maybe<number>,
): number => {
  if (!calculationUnit || isNil(calculationValue)) {
    return value;
  }

  if (calculationUnit === MeasureDisplayFormatOperator.Divide) {
    return value / calculationValue;
  }

  if (calculationUnit === MeasureDisplayFormatOperator.Multiply) {
    return value * calculationValue;
  }

  return value;
};

/**
 * Formats value according to measure format. Used for chart values
 *
 * @param num Numeric value
 * @param customMeasureFormat A custom measure format with optional unit
 * and calculation
 * @param ignoreCalculation OPTIONAL Boolean value to ignore calculation
 */
export const formatValueWithMeasureFormat = (
  num: number,
  customMeasureFormat: Maybe<IMeasureDisplayFormatFieldsFragment>,
  ignoreCalculation?: boolean,
): string => {
  let computedValue = num;
  if (!ignoreCalculation) {
    computedValue = computeMeasureValue(
      num,
      customMeasureFormat?.calculationUnit,
      customMeasureFormat?.calculationValue,
    );
  }

  // in Currency and Percent, there is no Unit available.
  if (customMeasureFormat?.kind === MeasureDisplayFormatKind.Currency) {
    const currencyValue = globalFormat.formatNumber(computedValue, {
      style: 'currency',
      decimalPlace: customMeasureFormat?.decimalPlace ?? 0,
    });
    return `${currencyValue}${getSuffixOnCalculatedValue(customMeasureFormat)}`;
  } else if (customMeasureFormat?.kind === MeasureDisplayFormatKind.Percent) {
    const percentageValue = globalFormat.formatNumber(computedValue, {
      style: 'percent',
      decimalPlace: customMeasureFormat?.decimalPlace ?? 0,
    });
    return `${percentageValue}${getSuffixOnCalculatedValue(
      customMeasureFormat,
    )}`;
  } else {
    // if number kind, add suffix and unit to the return string
    const numValue = globalFormat.formatNumber(computedValue, {
      style: 'decimal',
      decimalPlace: customMeasureFormat?.decimalPlace ?? 0,
    });
    if (customMeasureFormat?.unit) {
      return `${numValue} ${
        customMeasureFormat.unit
      }${getSuffixOnCalculatedValue(customMeasureFormat)}`;
    }
    return `${numValue}${getSuffixOnCalculatedValue(customMeasureFormat)}`;
  }
};

/**
 * Reuse @formatValueWithMeasureFormat to format value with yAxisFormat
 * Didn't import @formatValueWithMeasureFormat to avoid __typename casting in components
 */
export const formatValueWithYAxisFormat = (
  num: number,
  yAxisFormat: ICentralYAxisSettings,
) => {
  return formatValueWithMeasureFormat(
    num,
    {
      __typename: 'MeasureDisplayFormat',
      kind: yAxisFormat?.format.kind,
      decimalPlace: yAxisFormat.format.decimalPlace,
      calculationUnit: null,
      calculationValue: null,
      unit: null,
    },
    true,
  );
};

/**
 * Formats chart Y axis tick according to custom measure format
 * Result depends on kind, calculation, and num
 *
 * @param num Numeric value
 * @param measureDisplayFormat A custom measure format
 * @param forcePercent OPTIONAL Boolean value to override the output result
 */
export const formatYAxisTickWithCustomFormat = (
  num: number,
  measureDisplayFormat: Maybe<IMeasureDisplayFormatFieldsFragment>,
  forcePercent: boolean | undefined,
): string => {
  if (
    forcePercent ||
    measureDisplayFormat?.kind === MeasureDisplayFormatKind.Percent
  ) {
    return globalFormat.formatNumber(num, {
      ...defaultNumberFormatOptions.compact,
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  if (measureDisplayFormat?.kind === MeasureDisplayFormatKind.Currency) {
    return globalFormat.formatNumber(num, {
      ...defaultNumberFormatOptions.compact,
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  return globalFormat.formatNumber(num, {
    ...defaultNumberFormatOptions.compact,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export const formatCustomBarLabel = (
  num: number | undefined,
  measureDisplayFormat: Maybe<IMeasureDisplayFormatFieldsFragment>,
) => {
  if (isNil(num)) {
    return;
  }

  if (!measureDisplayFormat) {
    return formatValueTiny(num, 0);
  }

  const { kind, decimalPlace } = measureDisplayFormat;

  if (kind === MeasureDisplayFormatKind.Currency) {
    return formatValueTiny(num, decimalPlace);
  }

  if (kind === MeasureDisplayFormatKind.Percent) {
    return formatPercentTiny(num, decimalPlace);
  }

  return formatValueTiny(num, decimalPlace);
};

/**
 * Add suffix on calculation format value
 * if calculation is multiply, add  " ( * 10)"
 * if calculate is divide, add "( / 10)"
 */
export const getSuffixOnCalculatedValue = (
  measureFormat: Maybe<IMeasureDisplayFormatFieldsFragment>,
) => {
  if (
    measureFormat &&
    !isNil(measureFormat?.calculationUnit) &&
    !isNil(measureFormat?.calculationValue)
  ) {
    const { calculationUnit, calculationValue } = measureFormat;
    return ` ( ${measureDisplayFormatCalculationUnitLookup[calculationUnit]} ${calculationValue})`;
  }

  return '';
};
